import { useEffect, useRef } from 'react'

/**
 * Read a Zebra hardware scanner as what it actually is: a very fast keyboard.
 *
 * DataWedge, preinstalled on every Zebra Android device, types the decoded
 * barcode into whatever has focus and then presses Enter. Nothing here is
 * Zebra-specific — it works with any keyboard-wedge scanner, and typing a code
 * by hand and pressing Enter is indistinguishable from pulling the trigger.
 *
 * Characters are buffered until Enter delivers them. Keystrokes aimed at a
 * real form field are left alone: the field already receives them directly,
 * which is what lets manual entry and scanning share a screen without any
 * timing heuristics.
 *
 * Mount this only on the handheld screens. Nothing else in the app installs
 * it, so every other page — the camera putaway flow included — behaves exactly
 * as it did before.
 */

// A wedge fires keystrokes microseconds apart, so any real gap means the
// characters came from somewhere else. Discarding the partial buffer stops a
// stray keypress gluing itself to the front of the next scan.
export const SCAN_GAP_MS = 500

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

/**
 * @param {(code: string) => void} onScan  Called with the completed barcode.
 * @param {{enabled?: boolean}} [options]  Set `enabled: false` to pause, e.g.
 *   while a commit is in flight, without unmounting the screen.
 */
export default function useScanGun(onScan, { enabled = true } = {}) {
    const bufferRef = useRef('')
    const lastKeyAtRef = useRef(0)
    // Kept in a ref so a caller passing an inline arrow doesn't reattach the
    // listener on every render — and so the buffer survives those renders.
    const onScanRef = useRef(onScan)
    onScanRef.current = onScan

    useEffect(() => {
        if (!enabled) return undefined

        function handleKeyDown(event) {
            if (isTypingInAField(event.target) || hasModifier(event)) return

            if (event.key === 'Enter') {
                const code = bufferRef.current
                bufferRef.current = ''
                if (code) onScanRef.current(code)
                return
            }

            if (!isPrintable(event.key)) return

            const now = Date.now()
            if (now - lastKeyAtRef.current > SCAN_GAP_MS) bufferRef.current = ''
            lastKeyAtRef.current = now
            bufferRef.current += event.key
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            bufferRef.current = ''
        }
    }, [enabled])
}

function isTypingInAField(target) {
    if (!target || typeof target.closest !== 'function') return false
    if (EDITABLE_TAGS.has(target.tagName)) return true
    // `closest` rather than `isContentEditable` because the latter is unimplemented
    // in jsdom; it also covers a caret sitting inside a nested child of the
    // editable region, which a direct property read on the target would miss.
    const editable = target.closest('[contenteditable]')
    return Boolean(editable) && editable.getAttribute('contenteditable') !== 'false'
}

function hasModifier(event) {
    return event.ctrlKey || event.metaKey || event.altKey
}

/** Named keys like `Escape`, `ArrowLeft` and `F5` arrive as multi-character strings. */
function isPrintable(key) {
    return typeof key === 'string' && key.length === 1
}
